import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, SkipForward, X, Volume2, Languages } from "lucide-react";
import api from "@/lib/api";

const VOICE_LANG = { en: "en-IN", hi: "hi-IN", mr: "mr-IN", ta: "ta-IN" };
const LANG_LABELS = { en: "English", hi: "हिंदी", mr: "मराठी", ta: "தமிழ்" };

/**
 * AI Walkthrough overlay — Iter-110 + Iter-111.
 * Reads the process steps of a feature aloud (SpeechSynthesis API) with a
 * floating panel that shows the current step and Next / Prev / Close controls.
 * Iter-111 additions: language selector, resume-from-last-step, progress persistence.
 */
export default function AIWalkthroughOverlay() {
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const code = params.get("walkthrough");
  const [feature, setFeature] = useState(null);
  const [steps, setSteps] = useState(null);          // active language steps
  const [stepIdx, setStepIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("walkthrough_lang") || "en");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!code) { setFeature(null); setSteps(null); return; }
    api.get("/user-guide")
      .then(async (r) => {
        for (const s of r.data.sections) {
          for (const f of s.features) {
            if (f.code === code) {
              setFeature({ ...f, section: s.title });
              // Resume from last step if user watched this walkthrough previously
              try {
                const wt = await api.get("/user-guide/walkthroughs");
                const inProg = wt.data?.in_progress || {};
                if (inProg[code] != null && inProg[code] >= 0) setStepIdx(inProg[code]);
                else setStepIdx(0);
              } catch { setStepIdx(0); }
              return;
            }
          }
        }
      })
      .catch(() => {});
  }, [code]);

  // Fetch translated steps whenever lang or feature changes
  useEffect(() => {
    if (!feature) return;
    if (lang === "en") { setSteps(feature.steps); return; }
    setLoading(true);
    api.get(`/user-guide/steps-i18n/${feature.code}?lang=${lang}`)
       .then((r) => setSteps(r.data.steps))
       .catch(() => setSteps(feature.steps))
       .finally(() => setLoading(false));
  }, [feature, lang]);

  // Speak the current step
  useEffect(() => {
    if (!feature || !steps || !playing) return;
    try { window.speechSynthesis?.cancel(); } catch {}
    const text = `${stepIdx + 1}. ${steps[stepIdx]}`;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = VOICE_LANG[lang] || "en-IN";
    u.rate = 0.95; u.pitch = 1.0; u.volume = 1.0;
    u.onend = () => {
      if (stepIdx < steps.length - 1) setTimeout(() => setStepIdx(stepIdx + 1), 1200);
    };
    try { window.speechSynthesis?.speak(u); } catch {}
    return () => { try { window.speechSynthesis?.cancel(); } catch {} };
  }, [feature, steps, stepIdx, playing, lang]);

  // Persist progress
  useEffect(() => {
    if (!feature) return;
    if (stepIdx > 0) api.post(`/user-guide/walkthrough-progress/${feature.code}?step=${stepIdx}`).catch(() => {});
  }, [feature, stepIdx]);

  const close = () => {
    try { window.speechSynthesis?.cancel(); } catch {}
    const p = new URLSearchParams(loc.search); p.delete("walkthrough");
    nav(loc.pathname + (p.toString() ? "?" + p.toString() : ""), { replace: true });
  };

  const markDone = async () => {
    if (feature) try { await api.post(`/user-guide/walkthrough-complete/${feature.code}`); } catch {}
    close();
  };

  const changeLang = (v) => { setLang(v); localStorage.setItem("walkthrough_lang", v); };

  if (!feature || !steps) return null;
  const totalSteps = steps.length;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-[420px] max-w-[calc(100vw-32px)]" data-testid="walkthrough-overlay">
      <Card className="rounded-sm border-2 border-accent shadow-2xl overflow-hidden">
        <div className="bg-accent text-white px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Volume2 className="w-4 h-4 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase opacity-80">// AI walkthrough · {feature.section}</div>
              <div className="text-sm font-heading font-semibold truncate">{feature.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Languages className="w-3.5 h-3.5 opacity-80" />
            <select value={lang} onChange={(e) => changeLang(e.target.value)}
                     className="bg-transparent text-white text-xs font-mono uppercase border-0 focus:outline-none cursor-pointer"
                     data-testid="walkthrough-lang">
              {Object.entries(LANG_LABELS).map(([v, l]) => <option key={v} value={v} className="text-foreground">{l}</option>)}
            </select>
            <button onClick={close} className="hover:opacity-70" data-testid="walkthrough-close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Step {stepIdx + 1} of {totalSteps}{loading && " · translating…"}</div>
          <div className="text-sm leading-relaxed" data-testid="walkthrough-step">{steps[stepIdx]}</div>

          <div className="w-full h-1 rounded-full bg-border/40 overflow-hidden">
            <div className="h-full bg-accent transition-all duration-500" style={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }} />
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="text-[10px] font-mono text-muted-foreground">Copilot narrating · progress saved</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
                        disabled={stepIdx === 0} className="rounded-sm h-8 px-2" data-testid="walkthrough-prev">
                ← Prev
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPlaying(!playing)}
                        className="rounded-sm h-8 px-2" data-testid="walkthrough-play">
                {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </Button>
              {stepIdx < totalSteps - 1 ? (
                <Button size="sm" onClick={() => setStepIdx(stepIdx + 1)}
                          className="rounded-sm h-8 px-2 gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="walkthrough-next">
                  Next <SkipForward className="w-3.5 h-3.5" />
                </Button>
              ) : (
                <Button size="sm" onClick={markDone} className="rounded-sm h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="walkthrough-done">
                  Done ✓
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
